import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

/** Writes `value` under `key`, or takes the key off the query string when there is nothing to write. */
export const setOrDelete = (params: URLSearchParams, key: string, value: string) => {
    if (value) {
        params.set(key, value);
    } else {
        params.delete(key);
    }
};

/**
 * The one way a screen writes what it shows into its own query string: `change` edits a copy of the
 * current parameters, which then replace the current entry -- a lens or a filter is not a place the
 * browser's Back should step through. The location state goes along, since it says where the
 * screen's Retour leads. A change that leaves the parameters as they were touches nothing.
 *
 * The address is read when the change is written, not when the screen last rendered: two changes
 * written in the same turn both stay.
 */
export const useReplaceSearch = () => {
    const history = useHistory();

    return useCallback(
        (change: (params: URLSearchParams) => void) => {
            const { search, state } = history.location;
            const params = new URLSearchParams(search);
            change(params);

            if (params.toString() === new URLSearchParams(search).toString()) {
                return;
            }

            history.replace({ search: params.toString(), state });
        },
        [history],
    );
};
